import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';

import { TrialStatusResponse } from '@cast/types';
import { TrialStatusEnum } from '@cast/types-organization-management';

dayjs.extend(isBetween);
dayjs.extend(utc);

import { TrialState } from '../types';

export const isEligibleForTrial = (status?: TrialStatusEnum): boolean => {
  if (!status) {
    return false;
  }

  const eligibleStatuses: TrialStatusEnum[] = [
    TrialStatusEnum.PENDING,
    TrialStatusEnum.ACTIVE,
    TrialStatusEnum.EXPIRED, // Org can be eligible for another trial
  ];

  return eligibleStatuses.includes(status);
};

/**
 * Returns the number of calendar days remaining until the given date.
 * Returns 0 when the end date falls on today's calendar day.
 */
export const getExpiresInDays = (endDate: Dayjs | null | undefined): number => {
  if (!endDate?.isValid()) {
    return Infinity;
  }

  const now = dayjs();

  return endDate.utc().startOf('day').diff(now.utc().startOf('day'), 'day');
};

export const getTrialState = (data?: TrialStatusResponse): TrialState => {
  if (data?.inGracePeriod) {
    return 'grace-period';
  }

  if (
    data?.status === TrialStatusEnum.EXPIRED ||
    data?.status === TrialStatusEnum.CONVERTED
  ) {
    return 'post-trial';
  }

  const now = dayjs();

  if (data?.status === TrialStatusEnum.ACTIVE) {
    const trialStart = dayjs(data?.trialStartTime);
    const trialEnd = dayjs(data?.trialEndTime);

    if (
      trialStart.isValid() &&
      trialEnd.isValid() &&
      now.isBetween(trialStart, trialEnd, 'millisecond', '[]')
    ) {
      return 'active';
    }
  }

  return 'pre-trial';
};
